import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  TooltipProps
} from 'recharts';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { TimeSeriesData, CategoryAnalysis } from '../types';

interface ChartProps {
  type: 'line' | 'area' | 'bar' | 'pie' | 'doughnut';
  data: TimeSeriesData[] | CategoryAnalysis[];
  title: string;
  subtitle?: string;
  height?: number;
  className?: string;
  mainCurrencySymbol?: string;
  mainCurrencyCode?: string;
}

const CHART_COLORS = {
  income: '#10B981',     // Green - Emerald 500 (positive money flow)
  expenses: '#EF4444',   // Red - Red 500 (money going out)
  savings: '#3B82F6',    // Blue - Blue 500 (accumulated wealth)
  primary: '#6366F1',    // Indigo 500 (primary brand)
  secondary: '#8B5CF6',  // Violet 500 (secondary brand)
  warning: '#F59E0B',    // Amber 500 (warnings)
  info: '#06B6D4',       // Cyan 500 (information)
  purple: '#A855F7',     // Purple 500
  pink: '#EC4899',       // Pink 500
  orange: '#F97316',     // Orange 500
  teal: '#14B8A6',       // Teal 500
  lime: '#84CC16',       // Lime 500
};

// Dark theme optimized colors with better visibility
const DARK_CHART_COLORS = {
  income: '#34D399',     // Green - Emerald 400 (more visible in dark)
  expenses: '#F87171',   // Red - Red 400 (softer red for dark theme)
  savings: '#60A5FA',    // Blue - Blue 400 (more visible in dark)
  primary: '#818CF8',    // Indigo 400
  secondary: '#A78BFA',  // Violet 400
  warning: '#FBBF24',    // Amber 400
  info: '#22D3EE',       // Cyan 400
  purple: '#C084FC',     // Purple 400
  pink: '#F472B6',       // Pink 400
  orange: '#FB923C',     // Orange 400
  teal: '#2DD4BF',       // Teal 400
  lime: '#A3E635',       // Lime 400
};

const PIE_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Violet
  '#06B6D4', // Cyan
  '#EC4899', // Pink
  '#F97316', // Orange
  '#A855F7', // Purple
  '#14B8A6', // Teal
  '#84CC16', // Lime
  '#6366F1', // Indigo
];

const CATEGORY_COLORS = [
  '#3B82F6', // Blue - Housing/Rent
  '#10B981', // Green - Income/Salary
  '#F59E0B', // Amber - Food/Dining
  '#EF4444', // Red - Entertainment
  '#8B5CF6', // Violet - Transportation
  '#06B6D4', // Cyan - Utilities
  '#EC4899', // Pink - Shopping
  '#F97316', // Orange - Healthcare
  '#A855F7', // Purple - Education
  '#14B8A6', // Teal - Travel
  '#84CC16', // Lime - Subscriptions
  '#6366F1', // Indigo - Other
];

interface CustomTooltipProps extends TooltipProps<any, any> {
  formatValue?: (value: number) => string;
}

function CustomTooltip({ active, payload, label, formatValue }: CustomTooltipProps) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background-secondary/95 dark:bg-background-dark-secondary/95 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl shadow-lg p-3">
        <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2">
          {label}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center space-x-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
              {entry.name}: {formatValue ? formatValue(entry.value) : entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

export function Chart({ type, data, title, subtitle, height = 400, className, mainCurrencySymbol = '$', mainCurrencyCode = 'USD' }: ChartProps) {
  // Dynamic theme-aware colors with state management
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // Set initial theme state
    setIsDarkMode(document.documentElement.classList.contains('dark'));

    // Create observer for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          setIsDarkMode(document.documentElement.classList.contains('dark'));
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const themeColors = {
    gridColor: isDarkMode ? '#333840' : '#E5E7EB',
    textColor: isDarkMode ? '#A3A3A3' : '#6B7280',
    axisColor: isDarkMode ? '#4B5563' : '#9CA3AF',
  };

  const chartColors = isDarkMode ? DARK_CHART_COLORS : CHART_COLORS;

  const formatCurrency = (value: number) => {
    // Use simple symbol formatting for better performance and consistency
    if (value >= 1000000) {
      return `${mainCurrencySymbol}${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${mainCurrencySymbol}${(value / 1000).toFixed(1)}K`;
    } else {
      return `${mainCurrencySymbol}${value.toFixed(0)}`;
    }
  };

  const renderTimeSeriesChart = () => {
    const timeSeriesData = data as TimeSeriesData[];
    
    switch (type) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={timeSeriesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={themeColors.gridColor} />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12, fill: themeColors.textColor }}
                stroke={themeColors.axisColor}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: themeColors.textColor }}
                stroke={themeColors.axisColor}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip formatValue={formatCurrency} />} />
              <Legend 
                wrapperStyle={{ color: themeColors.textColor }}
                iconType="line"
              />
              <Line 
                type="monotone" 
                dataKey="income" 
                stroke={chartColors.income} 
                strokeWidth={3}
                dot={{ fill: chartColors.income, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: chartColors.income, strokeWidth: 2 }}
                name="Income"
              />
              <Line 
                type="monotone" 
                dataKey="expenses" 
                stroke={chartColors.expenses} 
                strokeWidth={3}
                dot={{ fill: chartColors.expenses, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: chartColors.expenses, strokeWidth: 2 }}
                name="Expenses"
              />
              <Line 
                type="monotone" 
                dataKey="savings" 
                stroke={chartColors.savings} 
                strokeWidth={3}
                dot={{ fill: chartColors.savings, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: chartColors.savings, strokeWidth: 2 }}
                name="Net Savings"
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={timeSeriesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={themeColors.gridColor} />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12, fill: themeColors.textColor }}
                stroke={themeColors.axisColor}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: themeColors.textColor }}
                stroke={themeColors.axisColor}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip formatValue={formatCurrency} />} />
              <Legend 
                wrapperStyle={{ color: themeColors.textColor }}
                iconType="rect"
              />
              <Area 
                type="monotone" 
                dataKey="income" 
                stackId="1"
                stroke={chartColors.income} 
                fill={chartColors.income}
                fillOpacity={0.7}
                name="Income"
              />
              <Area 
                type="monotone" 
                dataKey="expenses" 
                stackId="1"
                stroke={chartColors.expenses} 
                fill={chartColors.expenses}
                fillOpacity={0.7}
                name="Expenses"
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={timeSeriesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={themeColors.gridColor} />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12, fill: themeColors.textColor }}
                stroke={themeColors.axisColor}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: themeColors.textColor }}
                stroke={themeColors.axisColor}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip formatValue={formatCurrency} />} />
              <Legend 
                wrapperStyle={{ color: themeColors.textColor }}
                iconType="rect"
              />
              <Bar dataKey="income" fill={chartColors.income} name="Income" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" fill={chartColors.expenses} name="Expenses" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  const renderCategoryChart = () => {
    const categoryData = data as CategoryAnalysis[];
    
    if (type === 'pie' || type === 'doughnut') {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={categoryData}
              cx="50%"
              cy="50%"
              innerRadius={type === 'doughnut' ? 60 : 0}
              outerRadius={120}
              paddingAngle={2}
              dataKey="amount"
              nameKey="name"
            >
              {categoryData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.color || CATEGORY_COLORS[index % CATEGORY_COLORS.length]} 
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip formatValue={formatCurrency} />} />
            <Legend 
              verticalAlign="bottom" 
              height={36}
              wrapperStyle={{ color: themeColors.textColor }}
              formatter={(value, entry) => (
                <span style={{ color: entry.color }}>{value}</span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={categoryData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={themeColors.gridColor} />
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 12, fill: themeColors.textColor }}
            stroke={themeColors.axisColor}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis 
            tick={{ fontSize: 12, fill: themeColors.textColor }}
            stroke={themeColors.axisColor}
            tickFormatter={formatCurrency}
          />
          <Tooltip content={<CustomTooltip formatValue={formatCurrency} />} />
          <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
            {categoryData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.color || CATEGORY_COLORS[index % CATEGORY_COLORS.length]} 
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const isTimeSeriesData = (data: any[]): data is TimeSeriesData[] => {
    return data.length > 0 && 'date' in data[0];
  };

  return (
    <Card className={cn(
      "bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-sm hover:shadow-md transition-all duration-200",
      className
    )}>
      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
            {title}
          </h3>
          {subtitle && (
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
              {subtitle}
            </p>
          )}
        </div>
        
        <div className="w-full">
          {isTimeSeriesData(data) ? renderTimeSeriesChart() : renderCategoryChart()}
        </div>
      </div>
    </Card>
  );
} 